import {
  alpha,
  Card,
  Collapse,
  Fade,
  FormLabel,
  Grid,
  Link,
  MenuItem,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CardBody from "../../../../components/Card/CardBody";
import FormTextTC from "../../../../components/Forms/FormTextTC";
import FormSwitchTC from "../../../../components/Forms/FormSwitchTC";
import { useFormContext, useWatch } from "react-hook-form";
import { Alert, Skeleton } from "@mui/lab";
import TranslationWrapper from "../../../../components/TranslationWrapper/TranslationWrapper";
import axios from "axios";
import _ from "lodash";
import { CustomizableTexts } from "../../../../components/CustomizableTexts/CustomizableTexts";
import FormFile from "../../../../components/Forms/FormFile";
import { ImageResolutionHint } from "../../../../components/ImageHints/ImageResolutionHint";
import { UsingOtherSalesareaForStylingAlert } from "../../../../components/UsingOtherSalesareaForStylingAlert";
import { Check, Savings, Warning } from "@mui/icons-material";
import JamezzTableV2, { useReloadState } from "../../../../components/JamezzTable/V2/JamezzTableV2";
import { formatTime } from "../../../../licensing/utils/formatDate";
import { VestigingFormContext } from "../../VestigingForm";
import FormFieldTwoColumns from "../../../../components/Forms/Layouts/FormFieldTwoColumns";
import FormColor from "../../../../components/Forms/FormColor";
import PiggyRewardsV2 from "../../../../../../Domains/Piggy/Rewards/PiggyRewardsV2";
import FormSelectTC from "../../../../components/Forms/FormSelectTC";

type ResponseItem = {
  jamezz_product: {
    naam: string;
    id: number;
    is_actief: boolean;
    blocked: boolean;
  } | null;
  piggyItemName: string;
  artikelid: string;
  piggyItemId: number;
};

type ResponseItemTier = {
  uuid: string;
  name: string;
  description: string;
  position: number;
};
type Order = "asc" | "desc";

type Row = {
  status: string;
  recommendation: JSX.Element;
  piggyProductName: string;
  severity: "success" | "warning" | "error";
  icon: JSX.Element;
  behavior: JSX.Element;
  product: JSX.Element;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

/**
 * Taken from MUI documentation:
 * https://mui.com/material-ui/react-table/
 */
function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function PiggyTiersConfiguration({ vestigingId }: { vestigingId: number }) {
  const [tiers, setTiers] = useState<ResponseItemTier[]>([]);
  useEffect(() => {
    async function fetchPiggyTiers() {
      const res = await axios.get<{ data: ResponseItemTier[] }>(`core/piggy/listTiers?vestigingId=${vestigingId}`);
      setTiers(res.data.data.sort((a, b) => a.position - b.position));
    }

    fetchPiggyTiers();
  }, []);

  return (
    <>
      <h4 style={{ width: "100%" }}>Piggy tiers configuration</h4>
      <Grid item sm={5} style={{ paddingBottom: 2 }} xs={12}>
        <Typography variant={"body1"}>
          {/*<TranslationWrapper*/}
          {/*  tKey="vestigingen.form.piggy.configExplanation"*/}
          {/*  tDefault="All your piggy rewards are shown below. This shows if the article id is configured correctly in the piggy backoffice. Incorrectly configured rewards will not be shown to clients. You can change the article in the piggy backoffice."*/}
          {/*/>*/}
        </Typography>
      </Grid>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "32px" }}>
              <TranslationWrapper tDefault={"Tier name"} tKey={"vestigingen.piggy.form-tiers.table-head.name"} />
            </TableCell>
            <TableCell>
              <TranslationWrapper
                tDefault={"Tier description"}
                tKey={"vestigingen.piggy.form-tiers.table-head.description"}
              />
            </TableCell>
            <TableCell>
              <TranslationWrapper tDefault={"Color"} tKey={"vestigingen.piggy.form-tiers.table-head.color"} />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tiers.map((row) => (
            <TableRow
              sx={(theme) => ({
                // backgroundColor: alpha(theme.palette[row.severity].main, 0.1),
              })}
            >
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.description}</TableCell>
              <TableCell>
                <FormColor name={"custom_data.piggy.tiers." + row.uuid + ".color"} nullable />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

function PiggyProductsConfiguration({ vestigingId }: { vestigingId: number }) {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Row>("piggyProductName");
  const [piggyProducts, setPiggyProducts] = useState<ResponseItem[]>([]);
  const isLoading = _.isEqual([], piggyProducts);
  const hasPiggyProducts = piggyProducts.length > 0;

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Row) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const createSortHandler = (property: keyof Row) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  useEffect(() => {
    async function fetchPiggyProducts() {
      const res = await axios.get<{ data: ResponseItem[] }>(
        `core/piggyRewardJamezzProducts?vestigingId=${vestigingId}`
      );
      setPiggyProducts(res.data.data);
    }

    fetchPiggyProducts();
  }, []);

  const rows: Row[] = piggyProducts.map((piggyProduct) => {
    const jProduct = piggyProduct.jamezz_product;
    const hasProduct = jProduct != null;
    const jProductIsAvailable = hasProduct && !jProduct.blocked && jProduct.is_actief;
    const hasId = piggyProduct.artikelid !== "-1";

    const row: Row = {
      status: "",
      recommendation: <>unhandled</>,
      piggyProductName: piggyProduct.piggyItemName,
      severity: "success",
      icon: <Check color={"success"} />,
      product: <>unhandled</>,
      behavior: <>unhandled</>,
    };

    if (jProduct && jProductIsAvailable) {
      row.status = "1-Ok";
      row.behavior = (
        <TranslationWrapper
          tDefault={"This reward is available to customers"}
          tKey={"vestigingen.piggy.form.behavior.product-correct"}
        />
      );
      row.product = (
        <Link href={`/manager/producten/${jProduct.id}/edit`} target={"_blank"}>
          {jProduct.naam}
        </Link>
      );
      row.recommendation = <></>;
    }

    if (!hasId || (hasId && !hasProduct)) {
      row.status = "2-link-product";
      row.severity = "error";
      row.recommendation = (
        <TranslationWrapper
          tDefault={"Link the related Jamezz product to the piggy article"}
          tKey={"vestigingen.piggy.form.link-product"}
        />
      );
      row.icon = <Warning color={"error"} />;
      row.behavior = (
        <TranslationWrapper
          tDefault={"This reward is not available to customers"}
          tKey={"vestigingen.piggy.form.not-linked"}
        />
      );
      row.product = (
        <TranslationWrapper tDefault={"No product linked"} tKey={"vestigingen.piggy.form.product.not-linked"} />
      );
    }

    if (hasProduct && !jProductIsAvailable) {
      row.status = "3-unblock-and-activate";
      row.severity = "warning";
      row.recommendation = (
        <TranslationWrapper
          tDefault={"Unblock and activate the linked Jamezz product"}
          tKey={"vestigingen.piggy.form.unblock-and-activate"}
        />
      );
      row.icon = <Warning color={"warning"} />;
      row.behavior = (
        <TranslationWrapper
          tDefault={"This reward is not available to customers, the jamezz product is blocked, inactive or unavailable"}
          tKey={"vestigingen.piggy.form.behavior.blocked-or-inactive"}
        />
      );
      row.product = (
        <Link href={`/manager/producten/${jProduct.id}/edit`} target={"_blank"}>
          {jProduct.naam}
        </Link>
      );
    }

    return row;
  });

  // @ts-ignore
  const products = rows.sort(getComparator(order, orderBy)).map((row) => {
    return (
      <TableRow
        sx={(theme) => ({
          backgroundColor: alpha(theme.palette[row.severity].main, 0.1),
        })}
      >
        <TableCell>{row.icon}</TableCell>
        <TableCell>{row.piggyProductName}</TableCell>
        <TableCell>{row.product}</TableCell>
        <TableCell>{row.behavior}</TableCell>
        <TableCell>{row.recommendation}</TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <h4 style={{ width: "100%" }}>Piggy product configuration</h4>
      <Grid item sm={5} style={{ paddingBottom: 2 }} xs={12}>
        <Typography variant={"body1"}>
          <TranslationWrapper
            tDefault={
              "All your piggy rewards are shown below. This shows if the article id is configured correctly in the piggy backoffice. Incorrectly configured rewards will not be shown to clients. You can change the article in the piggy backoffice."
            }
            tKey={"vestigingen.form.piggy.configExplanation"}
          />
        </Typography>
      </Grid>
      {isLoading ? (
        [1, 2, 3].map((bla) => (
          <Skeleton key={bla} variant={"rectangular"}>
            <Alert>hoi</Alert>
          </Skeleton>
        ))
      ) : hasPiggyProducts ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "status" ? order : false} sx={{ width: "32px" }}>
                <TableSortLabel
                  active={orderBy === "status"}
                  direction={orderBy === "status" ? order : "asc"}
                  onClick={createSortHandler("status")}
                >
                  <TranslationWrapper tDefault={"Status"} tKey={"vestigingen.piggy.form.table-head.status"} />
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === "piggyProductName" ? order : false}>
                <TableSortLabel
                  active={orderBy === "piggyProductName"}
                  direction={orderBy === "piggyProductName" ? order : "asc"}
                  onClick={createSortHandler("piggyProductName")}
                >
                  <TranslationWrapper
                    tDefault={"Piggy reward name"}
                    tKey={"vestigingen.piggy.form.table-head.reward-name"}
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TranslationWrapper
                  tDefault={"Linked Jamezz product"}
                  tKey={"vestigingen.piggy.form.table-head.jproduct-name"}
                />
              </TableCell>
              <TableCell>
                <TranslationWrapper
                  tDefault={"Is this reward available to customers?"}
                  tKey={"vestigingen.piggy.form.table-head.reward-available"}
                />
              </TableCell>
              <TableCell>
                <TranslationWrapper
                  tDefault={"Recommendations"}
                  tKey={"vestigingen.piggy.form.table-head.recommendations"}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{products}</TableBody>
        </Table>
      ) : (
        <TranslationWrapper
          tDefault={"No products configured in Piggy yet"}
          tKey={"vestigingen.form.piggy.noPiggyRewards"}
        />
      )}
    </>
  );
}

export default function Piggy() {
  const { vestigingId, menukaartVestigingId }: VestigingFormContext = useFormContext();
  const piggyEnabled = useWatch({ name: "piggy__use_loyalty" });
  const piggyUseGiftcards = useWatch({ name: "piggy__use_giftcard" });
  const clientId = useWatch({ name: "piggy__client_id" });
  const clientSecret = useWatch({ name: "piggy__client_secret" });
  const shopId = useWatch({ name: "piggy__shop_id" });
  const creditsGiven = useWatch({ name: "piggy__credits_given" });
  const rewardsClaimed = useWatch({ name: "piggy__rewards_claimed" });
  const salesareaType = useWatch({ name: "salesarea_type" });
  const styling_vestiging_id = useWatch({ name: "styling_vestiging_id" });
  const paymentsEnabled = Boolean(useWatch({ name: "payDirect" }));
  const { reloadState, updateReloadState } = useReloadState();

  const piggyConfigured = Boolean(clientId?.length && clientSecret?.length && shopId?.length);

  const translatableTexts = [
    "menu page - save loyalty points button",
    "loyalty gift card - title",
    ...(salesareaType === "KIOSK"
      ? [
          // kiosk
          "loyalty rewards - redeem disabled message",
          "loyalty card scan - title",
          "loyalty card scan - description",
          "loyalty gift card - stepper name",
        ]
      : [
          // qr and qr-derivatives
          "loyalty page - title",
          "loyalty rewards - description",
          "loyalty rewards - account title",
          "login page - description",
          "register page - description",
          "login page - verification code name",
        ]),
  ];

  const renderHeaderCells = useCallback(
    () => [
      { id: "id", label: "#", numeric: true, disablePadding: false, sortable: true },
      {
        id: "product_id",
        label: <TranslationWrapper tDefault={"Product ID"} tKey={"PiggyPendingTransacion.product_id"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
      {
        id: "reward_uuid",
        label: <TranslationWrapper tDefault={"Piggy Reward"} tKey={"PiggyPendingTransacion.reward_uuid"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },

      {
        id: "contact_uuid",
        label: <TranslationWrapper tDefault={"Piggy Contact"} tKey={"PiggyPendingTransacion.contact_uuid"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
      {
        id: "created_at",
        label: <TranslationWrapper tDefault={"Created at"} tKey={"posSyncs.created_at"} />,
        numeric: false,
        disablePadding: false,
        sortable: false,
      },
    ],
    []
  );

  const renderRowCells = useCallback((row) => {
    return (
      <>
        <TableCell>{row.id}</TableCell>
        <TableCell>
          <Link href={`/manager/producten/${row.product_id}/edit`}>{row.product_id}</Link>
        </TableCell>
        <TableCell>{row.reward_uuid}</TableCell>
        <TableCell>{row.contact_uuid}</TableCell>
        <TableCell>{formatTime(new Date(row.created_at))}</TableCell>
      </>
    );
  }, []);

  const piggy__use_loyalty = useWatch({ name: "piggy__use_loyalty" });
  const piggyRewardsSystem = useWatch({ name: "piggy__custom_data.rewards_system" });

  return (
    <>
      <Collapse in={piggyEnabled ? piggyConfigured : null} mountOnEnter unmountOnExit>
        <Card style={{ padding: "8px 16px" }}>
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <div>
              <label>
                <TranslationWrapper tDefault={"Credits given"} tKey={"vestigingen.form.piggy.credits_given"} />
              </label>
              &nbsp;{creditsGiven ?? 0}
            </div>
            <div>
              <label>
                <TranslationWrapper tDefault={"Rewards claimed:"} tKey={"vestigingen.form.piggy.rewards_claimed"} />
              </label>
              &nbsp;{rewardsClaimed ?? 0}
            </div>
          </div>
        </Card>
      </Collapse>

      <Card style={{ marginTop: "16px" }}>
        <CardBody>
          <h3>
            <TranslationWrapper tDefault={"Piggy Setup"} tKey={"vestigingen.form.piggy.header.setup"} />
          </h3>
          <Slide direction={"down"} in={!paymentsEnabled} mountOnEnter unmountOnExit>
            <Alert severity={"warning"}>
              <TranslationWrapper
                tDefault={
                  "Payments (payDirect) are not enabled on this salesarea. Without it, customers cannot receive loyalty points. Enable it in the payments tab."
                }
                tKey={"vestigingen.form.piggy.warning.payments-not-enabled"}
              />
            </Alert>
          </Slide>
          <Slide direction={"down"} in={!piggyConfigured ? piggyEnabled : null} mountOnEnter unmountOnExit>
            <Alert severity={"warning"}>
              <TranslationWrapper
                tDefault={"Piggy is turned on, make sure the client id, secret and shop id are filled in correctly."}
                tKey={"vestigingen.form.piggy.piggy_configuration_warning"}
              />
            </Alert>
          </Slide>
          <FormSwitchTC
            name={"piggy__use_loyalty"}
            tDefault={"use piggy loyalty"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__use_loyalty"}
          />
          <Collapse in={piggy__use_loyalty}>
            <Stack>
              <FormSwitchTC
                defaultValue={true}
                name={"piggy__allow_anonymous_contact_identifiers"}
                tDefault={"Allow anonymous loyalty cards"}
                tKey={"piggy__allow_anonymous_contact_identifiers"}
              />
              <FormSwitchTC
                defaultValue={true}
                name={"piggy__show_loyalty_in_app"}
                tDefault={"Show loyalty in app"}
                tKey={"piggy__show_loyalty_in_app"}
              />

              <FormTextTC
                name={"piggy__custom_data.signUpUrl"}
                tDefault={"Sign up url"}
                tKey={"piggy__custom_data.signUpUrl"}
              />

              <FormFieldTwoColumns tDefault={"Signup image"} tKey={"extra_settings__kioskv5_piggy_signup_image"}>
                <>
                  <ImageResolutionHint res={"300x200"} />
                  <FormFile canCrop={false} name={"extra_settings__kioskv5_piggy_signup_image"} />
                </>
              </FormFieldTwoColumns>
            </Stack>
          </Collapse>

          <FormSwitchTC
            name={"piggy__use_giftcard"}
            tDefault={"use piggy giftcards at checkout"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__use_giftcard"}
          />
          <Collapse in={piggyUseGiftcards}>
            <FormSwitchTC
              defaultValue={true}
              name={"custom_data.kiosk.piggy.show_giftcards_as_step"}
              tDefault={"Show giftcards as step in kiosk"}
              tKey={"custom_data.kiosk.piggy.show_giftcards_as_step"}
            />
          </Collapse>
          <FormSwitchTC
            name={"piggy__use_prepaid"}
            tDefault={"use piggy prepaid at checkout"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__use_prepaid"}
          />

          <FormSwitchTC
            name={"piggy__use_vouchers"}
            tDefault={"use piggy vouchers"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__use_vouchers"}
          />
          <FormTextTC
            name={"piggy__client_id"}
            rules={{
              pattern: {
                value: /\d+/,
                message: "A Piggy client id should only contain digits, such as 456 or 2834",
              },
            }}
            tDefault={"client id"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__client_id"}
          />
          <FormTextTC
            name={"piggy__client_secret"}
            rules={{
              pattern: {
                value: /[a-zA-Z0-9]{30,100}/,
                message:
                  "Are you sure that client secret is correct? A client secret looks like p4Ln0XABxFe2iYdGqhSef...",
              },
            }}
            tDefault={"client secret"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__client_secret"}
          />
          <FormTextTC
            name={"piggy__shop_id"}
            rules={{
              pattern: {
                value: /[a-fA-F0-9-]{36}/,
                message: "Are you sure your shop id is correct? It should look like xxxxx-xxxx-xxxx-xxxx-xxxxxxxx",
              },
            }}
            tDefault={"shop id"}
            tKey={"form.primairbetallingsverkeer.label.pay_provider_sec__piggy__shop_id"}
          />
        </CardBody>
      </Card>

      <Fade in={piggyEnabled ? piggyConfigured : null} mountOnEnter unmountOnExit>
        <div>
          {salesareaType === "KIOSK" ? (
            <Card style={{ marginTop: "16px" }}>
              <CardBody>
                <h4>
                  <TranslationWrapper
                    tDefault={"Piggy kiosk configuration"}
                    tKey={"vestigingen.form.piggy.header.piggy_kiosk_configuration"}
                  />
                </h4>
                <FormSwitchTC
                  name={"piggy__rewards_redeeming_enabled"}
                  tDefault={"Rewards redeeming enabled"}
                  tKey={"vestigingen.form.piggy.kiosk_configuration.rewards_redeeming_enabled"}
                />
                <FormSelectTC
                  defaultValue={"V1"}
                  name={"piggy__custom_data.rewards_system"}
                  options={[
                    <MenuItem key={"V1"} value={"V1"}>
                      V1 (only products)
                    </MenuItem>,
                    <MenuItem key={"V2"} value={"V2"}>
                      V2 (vouchers)
                    </MenuItem>,
                  ]}
                  tDefault={"Piggy Rewards System"}
                  tKey={"vestigingen.form.piggy.kiosk_configuration.rewards_system"}
                />

                {styling_vestiging_id ? (
                  <UsingOtherSalesareaForStylingAlert styling_salesarea_id={styling_vestiging_id} />
                ) : null}
                <FormLabel>
                  <TranslationWrapper
                    tDefault={"Scan card page image"}
                    tKey={"vestigingen.form.piggy.images.piggy_scan_card_page_image"}
                  />
                </FormLabel>
                <ImageResolutionHint res={"936x360"} />
                <FormFile name={"extra_settings__kioskv5_piggy_scan_card_page_image"} />
              </CardBody>
            </Card>
          ) : null}
          <Card style={{ marginTop: "16px" }}>
            <CardBody>
              <PiggyTiersConfiguration vestigingId={menukaartVestigingId ?? vestigingId} />
            </CardBody>
          </Card>
          <Collapse in={piggyRewardsSystem == "V1" || piggyRewardsSystem == null} unmountOnExit>
            <Card style={{ marginTop: "16px" }}>
              <CardBody>
                <PiggyProductsConfiguration vestigingId={menukaartVestigingId ?? vestigingId} />
              </CardBody>
            </Card>
          </Collapse>
          <Collapse in={piggyRewardsSystem == "V2"} unmountOnExit>
            <Card style={{ marginTop: "16px" }}>
              <CardBody>
                <PiggyRewardsV2 />
              </CardBody>
            </Card>
          </Collapse>
          <Card style={{ marginTop: "16px" }}>
            <CardBody>
              <h4>
                <TranslationWrapper tDefault={"Piggy copy (texts)"} tKey={"vestigingen.form.piggy.header.copy"} />
              </h4>
              <CustomizableTexts translatableTexts={translatableTexts} vestigingId={vestigingId} />
            </CardBody>
          </Card>
        </div>
      </Fade>

      <Card style={{ marginTop: "16px" }}>
        <JamezzTableV2
          checkboxSelection={true}
          dense={true}
          endPoint={"core/piggyTransaction"}
          filters={[
            {
              field: "vestiging_id",
              operator: "=",
              value: vestigingId,
            },
          ]}
          headerIcon={<Savings />}
          reloadState={reloadState}
          renderCellsForRow={renderRowCells}
          renderHeaderCells={renderHeaderCells}
          renderRowActions={() => []}
          renderSelectionActions={() => ""}
          rowIsEditable={() => false}
          rowIsSelectable={() => false}
          tableTitle={
            <TranslationWrapper tDefault={"Piggy Pending Transactions"} tKey={"piggy.pending.transactions"} />
          }
          uuid={"PiggyPendingTransactions-diugf209jssd76jsx"}
        />
      </Card>
    </>
  );
}
